import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Search, Trash2, Plus, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input, Textarea } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { DateInputBR } from '@components/DateInputBR';
import { productionService } from '@services/productionService';
import { productService } from '@services/productService';
import { rawMaterialService } from '@services/rawMaterialService';
import {
  ProductionOrderItemCreateData,
  ProductionOrderSalesOrder,
} from '@/types/production';

type TargetType = 'product' | 'material';

interface TargetOption {
  id: number;
  code: string;
  description: string;
}

interface OrderItemRow {
  key: number;
  targetType: TargetType;
  targetId: number;
  code: string;
  description: string;
  quantity_planned: string;
  notes: string;
}

/** Navigation state vindo de Produtos a Fabricar (Gerar OP) */
interface GenerateOpNavState {
  salesOrderIds?: number[];
  suggestedItems?: {
    product_id: number;
    sku: string;
    description?: string;
    quantity: number;
  }[];
}

let rowKeySeq = 1;

const parseNumber = (value: string): number => {
  const parsed = parseFloat((value || '').replace(',', '.'));
  return isNaN(parsed) ? NaN : parsed;
};

/** Extrai AAAA-MM-DD de uma data ISO (com ou sem hora) */
const toDateOnly = (value?: string): string => {
  if (!value) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : '';
};

/** Formata data ISO para dd/mm/aaaa sem deslocamento de fuso (parse manual) */
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return '-';
};

/**
 * Formulário de Ordem de Produção — criação e edição.
 * Itens podem ser produtos de fabricação própria OU semiacabados.
 * Pode ser pré-preenchido via navigation state (Gerar OP a partir de Produtos a Fabricar).
 */
export const ProductionOrderFormView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const navState = (location.state || {}) as GenerateOpNavState;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Dados gerais
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [flockColor, setFlockColor] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Itens
  const [items, setItems] = useState<OrderItemRow[]>(() =>
    (navState.suggestedItems || []).map((s) => ({
      key: rowKeySeq++,
      targetType: 'product' as TargetType,
      targetId: s.product_id,
      code: s.sku,
      description: s.description || '',
      quantity_planned: String(s.quantity ?? 1),
      notes: '',
    }))
  );

  // Pedidos de venda vinculados
  const [salesOrderIds, setSalesOrderIds] = useState<number[]>(navState.salesOrderIds || []);
  const [salesOrderDetails, setSalesOrderDetails] = useState<
    Record<number, ProductionOrderSalesOrder>
  >({});
  const [salesOrderIdInput, setSalesOrderIdInput] = useState('');

  // Busca de itens
  const [targetType, setTargetType] = useState<TargetType>('product');
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState<TargetOption[]>([]);
  const [itemSearching, setItemSearching] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const itemBoxRef = useRef<HTMLDivElement>(null);

  // ============================================================
  // Carregamento (edição)
  // ============================================================
  useEffect(() => {
    if (isEditing && id) {
      loadOrder(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const loadOrder = async (orderId: string) => {
    try {
      setLoading(true);
      setError(null);

      const order = await productionService.getProductionOrder(parseInt(orderId));

      if (order.status !== 0 && order.status !== 1) {
        setError('Somente ordens Planejadas ou Em Produção podem ser editadas');
      }

      setCode(order.code);
      setDescription(order.description || '');
      setFlockColor(order.flock_color || '');
      setPlannedStartDate(toDateOnly(order.planned_start_date));
      setPlannedEndDate(toDateOnly(order.planned_end_date));
      setNotes(order.notes || '');
      setItems(
        (order.items || []).map((item) => ({
          key: rowKeySeq++,
          targetType: (item.product_id ? 'product' : 'material') as TargetType,
          targetId: item.product_id || item.raw_material_id || 0,
          code: item.target_code || String(item.product_id || item.raw_material_id),
          description: item.target_description || '',
          quantity_planned: String(item.quantity_planned ?? ''),
          notes: item.notes || '',
        }))
      );
      setSalesOrderIds((order.sales_orders || []).map((so) => so.order_id));
      setSalesOrderDetails(
        (order.sales_orders || []).reduce(
          (acc, so) => ({ ...acc, [so.order_id]: so }),
          {} as Record<number, ProductionOrderSalesOrder>
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordem de produção');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // Busca de itens (produto de fabricação própria ou semiacabado)
  // ============================================================
  useEffect(() => {
    const term = itemSearch.trim();
    if (term.length < 2) {
      setItemResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setItemSearching(true);

        if (targetType === 'product') {
          const { products } = await productService.searchProducts(term, 1, 20, {
            is_active: true,
          });
          const manufactured = (products || []).filter((p: any) => p.is_manufactured);
          setItemResults(
            manufactured.slice(0, 10).map((p: any) => ({
              id: p.id,
              code: p.sku,
              description: p.description,
            }))
          );
        } else {
          // Semiacabados (material_type = 3)
          const { rawMaterials } = await rawMaterialService.searchRawMaterials(term, 1, 10, {
            is_active: true,
            materialType: 3,
          });
          setItemResults(
            (rawMaterials || []).map((m) => ({
              id: m.id,
              code: m.code,
              description: m.description,
            }))
          );
        }
        setShowItemDropdown(true);
      } catch {
        setItemResults([]);
      } finally {
        setItemSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSearch, targetType]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (itemBoxRef.current && !itemBoxRef.current.contains(event.target as Node)) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================
  // Manipulação de itens
  // ============================================================
  const isAlreadyAdded = (option: TargetOption): boolean =>
    items.some((row) => row.targetType === targetType && row.targetId === option.id);

  const addItem = (option: TargetOption) => {
    setShowItemDropdown(false);
    setItemSearch('');
    setItemResults([]);

    if (isAlreadyAdded(option)) {
      setErrors((prev) => ({
        ...prev,
        items: `O item "${option.code}" já foi adicionado à ordem`,
      }));
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.items;
      return newErrors;
    });

    setItems((prev) => [
      ...prev,
      {
        key: rowKeySeq++,
        targetType,
        targetId: option.id,
        code: option.code,
        description: option.description,
        quantity_planned: '1',
        notes: '',
      },
    ]);
  };

  const removeItem = (key: number) => {
    setItems((prev) => prev.filter((row) => row.key !== key));
  };

  const updateItem = (key: number, field: 'quantity_planned' | 'notes', value: string) => {
    setItems((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };

  // ============================================================
  // Pedidos de venda vinculados
  // ============================================================
  const addSalesOrderId = () => {
    const parsed = parseInt(salesOrderIdInput, 10);
    if (isNaN(parsed) || parsed <= 0) {
      setErrors((prev) => ({ ...prev, sales_orders: 'Informe um ID de pedido válido' }));
      return;
    }
    if (salesOrderIds.includes(parsed)) {
      setErrors((prev) => ({ ...prev, sales_orders: `O pedido ${parsed} já está vinculado` }));
      return;
    }
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.sales_orders;
      return newErrors;
    });
    setSalesOrderIds((prev) => [...prev, parsed]);
    setSalesOrderIdInput('');
  };

  const removeSalesOrderId = (orderId: number) => {
    setSalesOrderIds((prev) => prev.filter((oid) => oid !== orderId));
  };

  const getSalesOrderLabel = (orderId: number): string => {
    const detail = salesOrderDetails[orderId];
    if (!detail) return `#${orderId}`;
    return detail.order_num_erp || detail.order_num_ecommerce || `#${orderId}`;
  };

  // ============================================================
  // Validação e salvamento
  // ============================================================
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (items.length === 0) {
      newErrors.items = 'A ordem deve ter pelo menos 1 item';
    } else {
      for (const row of items) {
        const qty = parseNumber(row.quantity_planned);
        if (isNaN(qty) || qty <= 0) {
          newErrors.items = `Quantidade planejada do item "${row.code}" deve ser maior que zero`;
          break;
        }
      }
    }

    if (plannedStartDate && plannedEndDate && plannedEndDate < plannedStartDate) {
      newErrors.planned_end_date = 'Data final não pode ser anterior à data inicial';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildItemsPayload = (): ProductionOrderItemCreateData[] =>
    items.map((row) => ({
      ...(row.targetType === 'product'
        ? { product_id: row.targetId }
        : { raw_material_id: row.targetId }),
      quantity_planned: parseNumber(row.quantity_planned),
      notes: row.notes.trim() || undefined,
    }));

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      const payload = {
        description: description.trim() || undefined,
        flock_color: flockColor.trim() || undefined,
        planned_start_date: plannedStartDate || undefined,
        planned_end_date: plannedEndDate || undefined,
        notes: notes.trim() || undefined,
        items: buildItemsPayload(),
        sales_order_ids: salesOrderIds,
      };

      if (isEditing && id) {
        await productionService.updateProductionOrder(parseInt(id), payload);
        navigate(`/production/orders/${id}`);
      } else {
        const created = await productionService.createProductionOrder(payload);
        navigate(`/production/orders/${created.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar ordem de produção');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/production/orders')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/production/orders')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">
            {isEditing ? 'Editar Ordem de Produção' : 'Nova Ordem de Produção'}
          </h1>
          <p className="text-slate-600">
            {isEditing
              ? `Altere os dados da ordem ${code}`
              : 'Defina os itens a produzir e o planejamento'}
          </p>
        </div>
      </div>

      {/* Erro geral */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6">
        {/* Dados gerais */}
        <Card>
          <CardHeader title="Dados Gerais" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Código"
                value={code}
                readOnly
                disabled
                placeholder="Gerado automaticamente (ex.: OP-2026-001)"
                helperText={!isEditing ? 'O código é gerado automaticamente ao salvar' : undefined}
              />
              <Input
                label="Descrição"
                placeholder="Ex.: Produção semanal de cabides flocados"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Input
                label="Cor do Floco"
                placeholder="Ex.: Preto, Bege, Rosa..."
                value={flockColor}
                onChange={(e) => setFlockColor(e.target.value)}
              />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-label block mb-1">Início Previsto</label>
                  <DateInputBR
                    className="input w-full"
                    value={plannedStartDate}
                    onChange={setPlannedStartDate}
                  />
                </div>
                <div>
                  <label className="text-label block mb-1">Fim Previsto</label>
                  <DateInputBR
                    className="input w-full"
                    value={plannedEndDate}
                    onChange={setPlannedEndDate}
                  />
                  {errors.planned_end_date && (
                    <p className="text-sm text-red-600 mt-1">{errors.planned_end_date}</p>
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <Textarea
                  label="Observações"
                  rows={2}
                  placeholder="Informações adicionais sobre a ordem de produção..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Itens */}
        <Card>
          <CardHeader
            title="Itens a Produzir"
            subtitle="Produtos de fabricação própria ou semiacabados (mínimo 1)"
          />
          <CardContent>
            <div className="space-y-4">
              {/* Tipo do item */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Tipo de item
                </label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="targetType"
                      checked={targetType === 'product'}
                      onChange={() => {
                        setTargetType('product');
                        setItemSearch('');
                        setItemResults([]);
                      }}
                      className="border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm">Produto de fabricação própria</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="targetType"
                      checked={targetType === 'material'}
                      onChange={() => {
                        setTargetType('material');
                        setItemSearch('');
                        setItemResults([]);
                      }}
                      className="border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="text-sm">Semiacabado</span>
                  </label>
                </div>
              </div>

              {/* Autocomplete de item */}
              <div className="relative" ref={itemBoxRef}>
                <Input
                  label="Adicionar item"
                  placeholder={
                    targetType === 'product'
                      ? 'Buscar produto por SKU ou descrição (mín. 2 caracteres)...'
                      : 'Buscar semiacabado por código ou descrição (mín. 2 caracteres)...'
                  }
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  onFocus={() => itemResults.length > 0 && setShowItemDropdown(true)}
                  startIcon={<Search size={20} />}
                />
                {itemSearching && (
                  <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                    <span className="animate-spin w-3 h-3 border border-blue-500 border-t-transparent rounded-full inline-block" />
                    Buscando...
                  </p>
                )}
                {showItemDropdown && itemResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                    {itemResults.map((option) => {
                      const alreadyAdded = isAlreadyAdded(option);
                      return (
                        <button
                          key={option.id}
                          type="button"
                          disabled={alreadyAdded}
                          className="w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => addItem(option)}
                        >
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                              {option.code}
                            </code>
                            {alreadyAdded && (
                              <span className="text-xs text-amber-600 font-medium">
                                Já adicionado
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-0.5">{option.description}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
                {showItemDropdown &&
                  !itemSearching &&
                  itemSearch.trim().length >= 2 &&
                  itemResults.length === 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 px-4 py-3 text-sm text-slate-500">
                      Nenhum item encontrado
                    </div>
                  )}
              </div>

              {errors.items && <p className="text-sm text-red-600">{errors.items}</p>}

              {/* Tabela de itens */}
              {items.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-slate-300 rounded-lg">
                  <p className="text-slate-500 text-sm">
                    Nenhum item adicionado. Use a busca acima para incluir produtos ou
                    semiacabados.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <th className="text-left">Código</th>
                      <th className="text-left">Descrição</th>
                      <th className="text-center">Tipo</th>
                      <th className="text-right">Qtde Planejada</th>
                      <th className="text-left">Observações</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </TableHeader>
                  <tbody>
                    {items.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>
                          <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                            {row.code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-900">{row.description}</p>
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              row.targetType === 'product'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}
                          >
                            {row.targetType === 'product' ? 'Produto' : 'Semiacabado'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <input
                            type="number"
                            className="border rounded px-2 py-1 w-28 text-right"
                            value={row.quantity_planned}
                            min={0}
                            step={1}
                            onChange={(e) =>
                              updateItem(row.key, 'quantity_planned', e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            type="text"
                            className="border rounded px-2 py-1 w-full"
                            placeholder="Observações..."
                            value={row.notes}
                            onChange={(e) => updateItem(row.key, 'notes', e.target.value)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(row.key)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Remover item"
                          >
                            <Trash2 size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pedidos de venda vinculados */}
        <Card>
          <CardHeader
            title="Pedidos de Venda Vinculados"
            subtitle="Pedidos que originaram esta ordem de produção (opcional)"
          />
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="w-48">
                  <Input
                    label="ID do pedido"
                    type="number"
                    min={1}
                    placeholder="Ex.: 1234"
                    value={salesOrderIdInput}
                    onChange={(e) => setSalesOrderIdInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSalesOrderId();
                      }
                    }}
                  />
                </div>
                <Button variant="outline" onClick={addSalesOrderId} icon={<Plus size={18} />}>
                  Adicionar
                </Button>
              </div>

              {errors.sales_orders && (
                <p className="text-sm text-red-600">{errors.sales_orders}</p>
              )}

              {salesOrderIds.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum pedido de venda vinculado.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {salesOrderIds.map((orderId) => {
                    const detail = salesOrderDetails[orderId];
                    return (
                      <span
                        key={orderId}
                        className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-full pl-3 pr-1 py-1 text-sm"
                      >
                        <span className="font-medium text-slate-800">
                          Pedido {getSalesOrderLabel(orderId)}
                        </span>
                        {detail?.customer_name && (
                          <span className="text-slate-500">— {detail.customer_name}</span>
                        )}
                        {detail?.order_date && (
                          <span className="text-slate-400 text-xs">
                            {formatDate(detail.order_date)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeSalesOrderId(orderId)}
                          className="p-1 text-slate-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors"
                          title="Remover vínculo"
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/production/orders')}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} icon={<Save size={20} />}>
            {saving ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Ordem de Produção'}
          </Button>
        </div>
      </div>
    </div>
  );
};
